import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockClockOutlinedIcon from "@mui/icons-material/LockClockOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";

import {
  getAgreementState,
  signAgreement,
  unlockAgreement,
  uploadSelfie,
} from "api/PaymentAgreement";
import logo from "assets/images/logo.png";
import { appLanguages } from "utils/Currency&Language";

import DocumentReader from "./components/document-reader";
import PassportUpload from "./components/passport-upload";
import SelfieCapture from "./components/selfie-capture";
import SignatureCapture from "./components/signature-capture";
import { createTranslator } from "./translations";

// ----------------------------------------------------------------------

const NAVY = "#0e3461";

const STAGE = {
  LOADING: "LOADING",
  INVALID: "INVALID",
  LOCKED_OUT: "LOCKED_OUT",
  ALREADY_SIGNED: "ALREADY_SIGNED",
  UNLOCK: "UNLOCK",
  SELFIE: "SELFIE",
  REVIEW: "REVIEW",
  DONE: "DONE",
};

function PageShell({ children, t, locale, onChangeLocale, progress }) {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f6f7fb" }}>
      <Box
        sx={{
          bgcolor: "#fff",
          borderBottom: "1px solid",
          borderColor: "grey.200",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Container maxWidth="md" sx={{ py: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ height: 36, gap: 1.5 }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <Box
                component="img"
                src={logo}
                alt="Satyam Holidays"
                sx={{ height: 18, width: "auto", display: "block" }}
              />
              <Typography
                variant="caption"
                noWrap
                sx={{
                  display: { xs: "none", sm: "block" },
                  pl: 1,
                  borderLeft: "1px solid",
                  borderColor: "grey.300",
                  color: NAVY,
                  fontWeight: 600,
                  fontSize: 12,
                  lineHeight: 1,
                }}
              >
                {t("documentTitle")}
              </Typography>
            </Stack>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={locale}
              onChange={(_, value) => value && onChangeLocale(value)}
              sx={{ flexShrink: 0 }}
            >
              {appLanguages.map((language) => (
                <ToggleButton
                  key={language.locale}
                  value={language.locale}
                  sx={{
                    px: 0.9,
                    py: 0,
                    height: 22,
                    minHeight: 22,
                    textTransform: "uppercase",
                    fontSize: 11,
                    lineHeight: 1,
                  }}
                >
                  {language.locale}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>

          {progress && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                height: 22,
                borderTop: "1px solid",
                borderColor: "grey.100",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  fontSize: 11,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {progress.label}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: 3,
                  borderRadius: 99,
                  bgcolor: "grey.200",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${progress.percent}%`,
                    height: "100%",
                    bgcolor: "primary.main",
                  }}
                />
              </Box>
            </Stack>
          )}
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pt: 2, pb: { xs: 3, md: 4 } }}>
        {children}
      </Container>

      <Container maxWidth="md" sx={{ pb: 4 }}>
        <Typography
          variant="caption"
          sx={{ color: "text.disabled", display: "block", textAlign: "center" }}
        >
          Satyam Holidays · {t("needHelp")} NL +31 70 711 3625 · SR +597 436 950
          · CW +5999 514 1040
        </Typography>
      </Container>
    </Box>
  );
}

function MessageCard({ icon, tone = "primary.main", title, body, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, sm: 5 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
        textAlign: "center",
      }}
    >
      <Stack spacing={2} alignItems="center">
        <Box sx={{ color: tone, display: "flex" }}>{icon}</Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>
          {title}
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", maxWidth: 460 }}
        >
          {body}
        </Typography>
        {children}
      </Stack>
    </Paper>
  );
}

// ----------------------------------------------------------------------

export default function PaymentAgreementPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const token = params.token || searchParams.get("token") || "";

  // Defaults to Dutch because the agreement itself is only issued in Dutch;
  // the toggle translates the guidance around it, not the legal text.
  const [locale, setLocale] = useState("nl");
  const t = useMemo(() => createTranslator(locale), [locale]);

  const [stage, setStage] = useState(STAGE.LOADING);
  const [state, setState] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [unlockError, setUnlockError] = useState(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);

  const [selfieBusy, setSelfieBusy] = useState(false);
  const [selfieProgress, setSelfieProgress] = useState(0);
  const [selfieError, setSelfieError] = useState(null);

  const [hasReadDocument, setHasReadDocument] = useState(false);
  const [consent, setConsent] = useState(false);
  const [signatureValid, setSignatureValid] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState(null);
  const [signedResult, setSignedResult] = useState(null);

  const signatureRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    if (!token) {
      setStage(STAGE.INVALID);
      return undefined;
    }

    getAgreementState(token)
      .then((result) => {
        if (cancelled) return;
        setState(result);
        setAttemptsRemaining(result.attemptsRemaining);

        if (result.alreadySigned) setStage(STAGE.ALREADY_SIGNED);
        else if (result.isLocked) setStage(STAGE.LOCKED_OUT);
        else setStage(STAGE.UNLOCK);
      })
      .catch(() => {
        if (!cancelled) setStage(STAGE.INVALID);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleUpload = async (file) => {
    setUploading(true);
    setUploadProgress(0);
    setUnlockError(null);

    try {
      const result = await unlockAgreement(token, file, (event) => {
        if (event.total) {
          setUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      });
      setAccessToken(result.accessToken);
      setAgreement(result.agreement);
      setStage(
        result.agreement?.selfieCaptured ? STAGE.REVIEW : STAGE.SELFIE
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setUnlockError(error.message);
      if (error.attemptsRemaining != null) {
        setAttemptsRemaining(error.attemptsRemaining);
      }
      if (error.code === "LOCKED") setStage(STAGE.LOCKED_OUT);
      if (error.code === "ALREADY_SIGNED") setStage(STAGE.ALREADY_SIGNED);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSelfie = async (blob) => {
    setSelfieBusy(true);
    setSelfieProgress(0);
    setSelfieError(null);

    try {
      await uploadSelfie(token, blob, accessToken, (event) => {
        if (event.total) {
          setSelfieProgress(Math.round((event.loaded / event.total) * 100));
        }
      });
      setStage(STAGE.REVIEW);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSelfieError(error.message);
      if (error.code === "ALREADY_SIGNED") setStage(STAGE.ALREADY_SIGNED);
    } finally {
      setSelfieBusy(false);
      setSelfieProgress(0);
    }
  };

  const handleSign = async () => {
    setSignError(null);

    if (!hasReadDocument) {
      setSignError(t("signErrorScroll"));
      return;
    }
    if (!consent) {
      setSignError(t("signErrorConsent"));
      return;
    }

    const signature = await signatureRef.current?.getSignature();
    if (!signature) {
      setSignError(t("signErrorEmpty"));
      return;
    }

    setSigning(true);
    try {
      const result = await signAgreement(token, {
        accessToken,
        signatureImage: signature.dataUrl,
        method: signature.method,
        consentAccepted: true,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setSignedResult(result);
      setStage(STAGE.DONE);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSignError(error.message);
    } finally {
      setSigning(false);
    }
  };

  const steps = [t("step1"), t("step2"), t("step3"), t("step4")];
  const activeStep =
    stage === STAGE.DONE
      ? 3
      : stage === STAGE.REVIEW
      ? 2
      : stage === STAGE.SELFIE
      ? 1
      : 0;
  const showProgress = [
    STAGE.UNLOCK,
    STAGE.SELFIE,
    STAGE.REVIEW,
    STAGE.DONE,
  ].includes(stage);
  const progress = showProgress
    ? {
        label: `${activeStep + 1}/${steps.length} · ${steps[activeStep]}`,
        percent: ((activeStep + 1) / steps.length) * 100,
      }
    : null;

  const shell = (children) => (
    <PageShell
      t={t}
      locale={locale}
      onChangeLocale={setLocale}
      progress={progress}
    >
      {children}
    </PageShell>
  );

  if (stage === STAGE.LOADING) {
    return shell(
      <Stack alignItems="center" spacing={2} sx={{ py: 10 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("loading")}
        </Typography>
      </Stack>
    );
  }

  if (stage === STAGE.INVALID) {
    return shell(
      <MessageCard
        icon={<ErrorOutlineIcon sx={{ fontSize: 56 }} />}
        tone="error.main"
        title={t("invalidTitle")}
        body={t("invalidBody")}
      />
    );
  }

  if (stage === STAGE.LOCKED_OUT) {
    return shell(
      <MessageCard
        icon={<LockClockOutlinedIcon sx={{ fontSize: 56 }} />}
        tone="warning.main"
        title={t("lockedTitle")}
        body={t("lockedBody")}
      />
    );
  }

  if (stage === STAGE.ALREADY_SIGNED) {
    return shell(
      <MessageCard
        icon={<CheckCircleIcon sx={{ fontSize: 56 }} />}
        tone="success.main"
        title={t("alreadySignedTitle")}
        body={t("alreadySignedBody")}
      />
    );
  }

  if (stage === STAGE.DONE) {
    const emailedTo =
      signedResult?.emailedTo || agreement?.payer?.email || "";

    return shell(
      <MessageCard
        icon={<CheckCircleIcon sx={{ fontSize: 60 }} />}
        tone="success.main"
        title={t("doneHeading")}
        body={t("doneBody")}
      >
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
            p: 2,
            borderRadius: 2,
            bgcolor: "rgba(14, 52, 97, 0.06)",
            textAlign: "left",
            width: "100%",
            maxWidth: 460,
          }}
        >
          <MarkEmailReadOutlinedIcon
            sx={{ color: NAVY, mt: 0.25, flexShrink: 0 }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ color: NAVY, mb: 0.25 }}>
              {t("doneEmailTitle")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("doneEmailBody", { email: emailedTo })}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 0.5,
            p: 2.5,
            borderRadius: 2,
            bgcolor: "#f6f7fb",
            textAlign: "left",
            width: "100%",
            maxWidth: 460,
          }}
        >
          <Typography variant="subtitle2" sx={{ color: NAVY, mb: 1 }}>
            {t("doneNextTitle")}
          </Typography>
          <Stack component="ol" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
            {[t("doneNext1"), t("doneNext2"), t("doneNext3")].map((line) => (
              <Typography
                key={line}
                component="li"
                variant="body2"
                sx={{ color: "text.secondary", fontSize: 13.5 }}
              >
                {line}
              </Typography>
            ))}
          </Stack>
        </Box>
      </MessageCard>
    );
  }

  return shell(
    <Stack spacing={2}>
      {stage === STAGE.UNLOCK && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <PassportUpload
            t={t}
            payerFirstName={state?.payerFirstName}
            attemptsRemaining={attemptsRemaining}
            busy={uploading}
            progress={uploadProgress}
            error={unlockError}
            onUpload={handleUpload}
            onDismissError={() => setUnlockError(null)}
          />
        </Paper>
      )}

      {stage === STAGE.SELFIE && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <SelfieCapture
            t={t}
            busy={selfieBusy}
            progress={selfieProgress}
            error={selfieError}
            onConfirm={handleSelfie}
          />
        </Paper>
      )}

      {stage === STAGE.REVIEW && agreement && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ sm: "center" }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    fontWeight: 700,
                    color: "text.disabled",
                    fontSize: 10,
                  }}
                >
                  {t("amountLabel")}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, color: "primary.main", lineHeight: 1.1 }}
                >
                  {agreement.payment?.amountFormatted}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {agreement.payment?.service}
                </Typography>
              </Box>

              <Stack spacing={1} alignItems={{ xs: "flex-start", sm: "flex-end" }}>
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<VerifiedUserOutlinedIcon />}
                  label={t("verifiedAs", {
                    name:
                      agreement.passport?.fullName ||
                      agreement.payer?.fullName ||
                      "",
                  })}
                  sx={{ maxWidth: "100%" }}
                />
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                  {t("referenceLabel")}: {agreement.reference}
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Typography variant="h6" sx={{ color: NAVY, mb: 0.5 }}>
              {t("reviewHeading")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 2, fontSize: 14 }}
            >
              {t("reviewIntro")}
            </Typography>

            <DocumentReader
              t={t}
              html={agreement.documentHtml}
              onReadToEnd={() => setHasReadDocument(true)}
            />
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: "grey.200",
              opacity: hasReadDocument ? 1 : 0.6,
              pointerEvents: hasReadDocument ? "auto" : "none",
              transition: "opacity 0.3s",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <DrawOutlinedIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" sx={{ color: NAVY }}>
                {t("signHeading")}
              </Typography>
            </Stack>

            <SignatureCapture
              ref={signatureRef}
              t={t}
              disabled={!hasReadDocument || signing}
              onValidityChange={setSignatureValid}
            />

            <Divider sx={{ my: 2.5, borderStyle: "dashed" }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={consent}
                  onChange={(event) => setConsent(event.target.checked)}
                  disabled={signing}
                  sx={{ mt: -1 }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: 13.5 }}>
                  {t("consentLabel")}
                </Typography>
              }
              sx={{ alignItems: "flex-start", ml: 0, mr: 0 }}
            />

            {signError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {signError}
              </Alert>
            )}

            <Button
              fullWidth
              size="large"
              variant="contained"
              disabled={!consent || !signatureValid || signing}
              onClick={handleSign}
              startIcon={
                signing ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <DrawOutlinedIcon />
                )
              }
              sx={{
                mt: 2.5,
                py: 1.6,
                borderRadius: 2,
                textTransform: "none",
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              {signing ? t("signing") : t("signCta")}
            </Button>
          </Paper>
        </>
      )}
    </Stack>
  );
}
